"use client";

import { useMemo, useState } from "react";
import {
  amortisationSchedule,
  calculateMortgage,
  formatSgd,
} from "@/lib/calculators/mortgage";
import {
  BANK_LOAN,
  CALCULATOR_DEFAULTS,
  REFERENCE_LINKS,
} from "@/lib/calculators/sgRates";

const PRICE_MIN = 100_000;
const PRICE_MAX = 20_000_000;
/**
 * The slider is not linear. Half the track covers everything up to the knee,
 * because nearly every Singapore home sits below it, and the other half
 * stretches to PRICE_MAX for the top of the market. A straight 100k-to-20M
 * track would squeeze the common range into the first few pixels.
 */
const PRICE_KNEE = 3_000_000;
const SLIDER_STEPS = 1000;
const SLIDER_MID = SLIDER_STEPS / 2;

const sliderToPrice = (pos: number) => {
  if (pos <= SLIDER_MID) {
    const raw = PRICE_MIN + (pos / SLIDER_MID) * (PRICE_KNEE - PRICE_MIN);
    return Math.round(raw / 10_000) * 10_000;
  }
  const raw =
    PRICE_KNEE +
    ((pos - SLIDER_MID) / SLIDER_MID) * (PRICE_MAX - PRICE_KNEE);
  return Math.round(raw / 100_000) * 100_000;
};

const priceToSlider = (price: number) => {
  if (price <= PRICE_KNEE) {
    return ((price - PRICE_MIN) / (PRICE_KNEE - PRICE_MIN)) * SLIDER_MID;
  }
  return (
    SLIDER_MID +
    ((price - PRICE_KNEE) / (PRICE_MAX - PRICE_KNEE)) * SLIDER_MID
  );
};

/** Turns 1500000 into "1,500,000" for the text field. */
const withCommas = (value: number) =>
  Number.isFinite(value) ? value.toLocaleString("en-SG") : "";

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <label className="block text-sm font-semibold text-ink">{label}</label>
      {children}
      {hint && <p className="text-xs text-ink-faint leading-relaxed">{hint}</p>}
    </div>
  );
}

/** Range input plus a matching number box, kept in sync. */
function SliderRow({
  value,
  min,
  max,
  step,
  suffix,
  ariaLabel,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  step: number;
  suffix: string;
  /** Both inputs need their own name — the visible <label> above them belongs
   *  to the Field, so a screen reader announces nothing without this. */
  ariaLabel: string;
  onChange: (next: number) => void;
}) {
  /** The box holds what is being typed, so a half-typed "1." stays as it is
   *  instead of collapsing to 0 and leaving a stray zero in front. */
  const [draft, setDraft] = useState<string | null>(null);

  const handleTyped = (raw: string) => {
    if (!/^\d*\.?\d*$/.test(raw)) return;
    setDraft(raw);
    if (raw === "" || raw === ".") return;
    onChange(clamp(Number(raw), min, max));
  };

  return (
    <div className="flex items-center gap-4">
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => {
          setDraft(null);
          onChange(Number(e.target.value));
        }}
        className="flex-1 accent-brand-600 cursor-pointer"
        aria-label={`${ariaLabel} slider`}
        aria-valuetext={`${value}${suffix}`}
      />
      <div className="flex items-center gap-1 rounded-lg border border-line bg-white px-3 py-2 w-[104px] shrink-0">
        <input
          type="text"
          inputMode="decimal"
          value={draft ?? String(value)}
          onChange={(e) => handleTyped(e.target.value)}
          onBlur={() => setDraft(null)}
          className="w-full text-right text-sm font-semibold text-ink outline-none"
          aria-label={ariaLabel}
        />
        <span className="text-sm text-ink-faint">{suffix}</span>
      </div>
    </div>
  );
}

export default function MortgageCalculator() {
  const [price, setPrice] = useState<number>(
    CALCULATOR_DEFAULTS.propertyPrice
  );
  /** Same reason as the slider boxes: an empty box must stay empty while it is
   *  being retyped, not snap to 0 and sit in front of the new digits. */
  const [priceDraft, setPriceDraft] = useState<string | null>(null);
  const [loanPercent, setLoanPercent] = useState<number>(
    CALCULATOR_DEFAULTS.loanPercent
  );
  const [interestRate, setInterestRate] = useState<number>(
    CALCULATOR_DEFAULTS.interestRate
  );
  const [tenureYears, setTenureYears] = useState<number>(
    CALCULATOR_DEFAULTS.tenureYears
  );

  // Rebuilt inside the memo so the dependency list stays the four raw
  // inputs. A shared object literal would be a new reference every render.
  const result = useMemo(
    () =>
      calculateMortgage({
        propertyPrice: price,
        loanPercent,
        interestRate,
        tenureYears,
      }),
    [price, loanPercent, interestRate, tenureYears]
  );
  const schedule = useMemo(
    () =>
      amortisationSchedule({
        propertyPrice: price,
        loanPercent,
        interestRate,
        tenureYears,
      }),
    [price, loanPercent, interestRate, tenureYears]
  );

  const principalShare = result.monthlyPayment
    ? (result.firstPaymentPrincipal / result.monthlyPayment) * 100
    : 0;
  const interestShare = 100 - principalShare;

  const peakYear = schedule.reduce(
    (max, y) => Math.max(max, y.principalPaid + y.interestPaid),
    0
  );

  return (
    <div className="space-y-6 lg:space-y-8">
      {/* ---------------- Inputs ---------------- */}
      <div className="rounded-2xl border border-line bg-white p-5 sm:p-7 grid gap-7 lg:grid-cols-2 lg:gap-x-10">
        <Field label="Property price">
          <div className="flex items-center gap-2 rounded-lg border border-line bg-white px-4 py-3 focus-within:border-brand-500">
            <span className="text-ink-faint font-medium">S$</span>
            <input
              inputMode="numeric"
              value={priceDraft ?? withCommas(price)}
              onChange={(e) => {
                const digits = e.target.value.replace(/[^\d]/g, "");
                setPriceDraft(digits === "" ? "" : withCommas(Number(digits)));
                if (digits !== "") {
                  setPrice(clamp(Number(digits), 0, PRICE_MAX));
                }
              }}
              onBlur={() => {
                setPriceDraft(null);
                setPrice((p) => (p < PRICE_MIN ? PRICE_MIN : p));
              }}
              className="w-full text-lg font-bold text-ink outline-none"
              aria-label="Property price in Singapore dollars"
            />
          </div>
          <input
            type="range"
            min={0}
            max={SLIDER_STEPS}
            step={1}
            value={priceToSlider(clamp(price, PRICE_MIN, PRICE_MAX))}
            onChange={(e) => {
              setPriceDraft(null);
              setPrice(sliderToPrice(Number(e.target.value)));
            }}
            className="w-full accent-brand-600 cursor-pointer mt-3"
            aria-label="Property price slider"
            aria-valuetext={formatSgd(price)}
          />
          <div className="flex justify-between text-xs text-ink-faint mt-1">
            <span>S$100k</span>
            <span>S$3m</span>
            <span>S$20m</span>
          </div>
        </Field>

        <Field
          label="Loan amount"
          hint="Choose how much of the property price you want to borrow."
        >
          <SliderRow
            value={loanPercent}
            min={0}
            max={100}
            step={1}
            suffix="%"
            ariaLabel="Loan amount as a percentage of the price"
            onChange={setLoanPercent}
          />
          <p className="text-sm text-ink-muted pt-1">
            {formatSgd(result.loanAmount)} borrowed,{" "}
            {formatSgd(result.downpayment)} paid up front.
          </p>
        </Field>

        <Field
          label="Interest rate"
          hint="Enter the interest rate quoted by your bank."
        >
          <SliderRow
            value={interestRate}
            min={0}
            max={8}
            step={0.05}
            suffix="%"
            ariaLabel="Interest rate"
            onChange={setInterestRate}
          />
        </Field>

        <Field
          label="Loan tenure"
          hint="Choose how long you want to take to repay the loan."
        >
          <SliderRow
            value={tenureYears}
            min={1}
            max={BANK_LOAN.maxTenureYears}
            step={1}
            suffix="yrs"
            ariaLabel="Loan tenure in years"
            onChange={setTenureYears}
          />
        </Field>
      </div>

      {/* ---------------- Results ---------------- */}
      {/* This H2 exists so the two H3s below it sit under a heading of their
          own level. Without it they were the first headings after the page
          H1, which reads as a broken outline to a crawler and a screen
          reader alike. */}
      <h2 className="font-display text-xl sm:text-2xl font-bold text-ink">
        Your loan breakdown
      </h2>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl bg-ink text-white p-5 sm:p-7">
          <p className="text-sm text-white/70">Estimated monthly repayment</p>
          <p className="font-display text-4xl sm:text-5xl font-bold mt-1">
            {formatSgd(result.monthlyPayment)}
          </p>
          <p className="text-sm text-white/70 mt-1">
            over {tenureYears} {tenureYears === 1 ? "year" : "years"} at{" "}
            {interestRate}%
          </p>

          <div className="mt-6">
            <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-white/15">
              <div
                className="bg-brand-500"
                style={{ width: `${principalShare}%` }}
              />
              <div
                className="bg-gold-500"
                style={{ width: `${interestShare}%` }}
              />
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3 text-sm">
              <span className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-brand-500" />
                Principal {formatSgd(result.firstPaymentPrincipal)}
              </span>
              <span className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-gold-500" />
                Interest {formatSgd(result.firstPaymentInterest)}
              </span>
            </div>
            <p className="text-xs text-white/60 mt-2">
              Split shown for your first payment. Interest falls and principal
              rises every month after that.
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-line bg-white p-5 sm:p-7">
          <h3 className="font-display text-lg font-bold text-ink">
            What the purchase costs you
          </h3>
          <dl className="mt-4 divide-y divide-line">
            {[
              ["Loan amount", formatSgd(result.loanAmount)],
              ["Downpayment", formatSgd(result.downpayment)],
              ["Total interest over the loan", formatSgd(result.totalInterest)],
              ["Total repaid to the bank", formatSgd(result.totalPaid)],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between gap-4 py-3">
                <dt className="text-sm text-ink-muted">{label}</dt>
                <dd className="text-sm font-bold text-ink text-right">
                  {value}
                </dd>
              </div>
            ))}
          </dl>
          <p className="text-xs text-ink-faint mt-4 leading-relaxed">
            Other purchase costs sit on top of your downpayment.
          </p>
        </div>
      </div>

      {/* ---------------- Chart ---------------- */}
      {schedule.length > 0 && (
        <div className="w-full rounded-2xl border border-line bg-white p-5 sm:p-7">
          <h3 className="font-display text-lg font-bold text-ink">
            How the balance falls each year
          </h3>
          <p className="text-sm text-ink-muted mt-1">
            Each bar is one year. Blue is principal, gold is interest.
          </p>
          <div
            className="flex w-full items-end gap-1 h-[280px] sm:h-[360px] mt-5"
            role="img"
            aria-label={`Yearly repayment split over ${schedule.length} years. Interest starts high and falls each year while principal rises.`}
          >
            {schedule.map((y) => {
              const total = y.principalPaid + y.interestPaid;
              const height = peakYear ? (total / peakYear) * 100 : 0;
              const pShare = total ? (y.principalPaid / total) * 100 : 0;
              return (
                <div
                  key={y.year}
                  className="flex-1 flex flex-col justify-end min-w-[2px]"
                  style={{ height: `${height}%` }}
                  title={`Year ${y.year}: principal ${formatSgd(
                    y.principalPaid
                  )}, interest ${formatSgd(y.interestPaid)}`}
                >
                  <div
                    className="bg-gold-500 rounded-t-[2px]"
                    style={{ height: `${100 - pShare}%` }}
                  />
                  <div
                    className="bg-brand-500"
                    style={{ height: `${pShare}%` }}
                  />
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-xs text-ink-faint mt-2">
            <span>Year 1</span>
            <span>Year {schedule.length}</span>
          </div>
        </div>
      )}

      <p className="text-xs text-ink-faint leading-relaxed px-1">
        These figures are an estimate for planning. Your bank sets the final
        numbers. For the rules on loan limits and tenure, see{" "}
        <a
          href={REFERENCE_LINKS.masPropertyLoans}
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand-600 underline"
        >
          MAS
        </a>{" "}
        and{" "}
        <a
          href={REFERENCE_LINKS.hdbLoan}
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand-600 underline"
        >
          HDB
        </a>
        .
      </p>
    </div>
  );
}
